import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CellContext } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { classNames } from 'support/helpers/generic/generic';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';

import { useDataTableContext } from '../../../contexts/DataTableContext';
import { DisplayModes, WebEDIRowData } from '../../../Types';
import { CellProps } from '../../types';
import {
  getCellNestedLevel,
  isProcurosAddedValue,
  useCellClassNames,
  useLineItemsTransportUnitsConfig,
} from '../cellHelpers';
import { DropdownCellReadOnly, DropdownCellReadOnlyProps } from './DropdownCellReadOnly';

type DropdownCellProps = {
  options: Array<{
    label: string;
    value: string | number | boolean;
  }>;
};
export type Props = CellContext<WebEDIRowData, unknown> & Omit<CellProps, 'register'> & DropdownCellProps;

export const DropdownCell = (props: Props) => {
  const { fieldId, column, table, row, isHighlighted, mode, options } = props;
  const { columnDef } = column;
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const isAdded = isProcurosAddedValue(row);
  const { isReadOnly: isTransportUnitReadOnly } = useLineItemsTransportUnitsConfig(fieldId, row?.original);

  const isCellReadOnly = Boolean(columnDef.meta?.rowConfig?.[row.index]?.is_readonly);
  const readOnly =
    isTransportUnitReadOnly ||
    (Boolean(columnDef.meta?.readOnly || isCellReadOnly) && !isAdded && mode !== DisplayModes.edit);
  const disabled = isSubmitting;

  const level = getCellNestedLevel(row);
  const isNested = level > 1;
  const { service, ErrorModalComponent, useErrorModal } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const fieldState = useFieldState(fieldId, error);
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();

  useEffect(() => {
    if (error && showErrorModal) {
      setCurrentError(error);
    }
  }, [error, setCurrentError, showErrorModal]);

  const onFocus = useCallback(() => {
    setShowErrorModal(true);
  }, [setShowErrorModal]);

  const onBlur = useCallback(() => {
    setShowErrorModal(false);
  }, [setShowErrorModal]);

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  const value = useWatch({ name: fieldId });
  const previousValue = useRef<string | undefined>(value);
  const { setValue } = useDataTableContext();
  useEffect(() => {
    if (!value && previousValue.current) {
      setValue(fieldId, previousValue.current);
    }
  }, [value, previousValue, setValue, fieldId]);

  useEffect(() => {
    let isCancelled = false;
    if (isModificationType(fieldId) && !value && !previousValue.current && options.length > 0) {
      setTimeout(() => {
        if (!isCancelled) {
          previousValue.current = options[0].value as string;
          setValue(fieldId, options[0].value);
        }
      }, 100);
    }

    return () => {
      isCancelled = true;
    };
  }, [value, fieldId, options, setValue]);

  if (readOnly) {
    return (
      <DropdownReadOnly
        cellProps={props}
        fieldId={fieldId}
        options={options}
        isNotShippedItemTransportUnit={isTransportUnitReadOnly}
      />
    );
  }

  return (
    <div
      ref={refs.setReference}
      className={classNames(
        'flex items-center w-full h-full p-1.5 border border-transparent',
        {
          'bg-red-50': fieldState === 'error',
          'bg-blue-50': fieldState === 'pending-validation',
          'bg-amber-100': isHighlighted,
        },
        cellClassNames,
      )}
    >
      <Dropdown
        disabled={disabled || (columnDef.meta?.disableIfNested && isNested)}
        onChange={(value) => {
          if (value) {
            previousValue.current = value;
            setValue(fieldId, value, {
              shouldDirty: true,
            });
          }
        }}
        value={value || previousValue.current}
        onFocus={onFocus}
        onOpen={onFocus}
        onBlur={onBlur}
        options={options}
        fieldState={fieldState}
        name={fieldId}
        size="extra-small"
        buttonClassName="rounded-3xl"
        wrapperClassName="flex-1"
      />
      {showErrorModal && ErrorModalComponent && (
        <ErrorModalComponent
          error={error}
          viewMode={false}
          ref={refs.setFloating}
          style={floatingStyles}
          numberOfErrors={numberOfErrors}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={columnDef.meta?.description}
        />
      )}
    </div>
  );
};

const useViewForm = ({ fieldId, options }: { fieldId: string; options: DropdownCellProps['options'] }) => {
  const value = useWatch({ name: fieldId });
  const valueForReadonly = useMemo(() => {
    return options?.find((option) => option.value === value)?.label;
  }, [value, options]);

  return useForm({ values: { [fieldId]: valueForReadonly } });
};

const DropdownReadOnly = ({
  fieldId,
  options,
  cellProps,
  isNotShippedItemTransportUnit,
}: {
  fieldId: string;
  options: DropdownCellProps['options'];
  cellProps: Props;
  isNotShippedItemTransportUnit: DropdownCellReadOnlyProps['isNotShippedItemTransportUnit'];
}) => {
  const formMethods = useViewForm({ fieldId, options });
  return (
    <FormProvider {...formMethods}>
      <DropdownCellReadOnly {...cellProps} isNotShippedItemTransportUnit={isNotShippedItemTransportUnit} />
    </FormProvider>
  );
};

//in case it is type of modification, set by default the first value
const isModificationType = (fieldId: string | undefined) => {
  return fieldId?.includes('.type') && fieldId?.includes('modification_groups');
};
